/**
 * Spec 10.2.1 §2.4 — reconciliation workbench. The variance tile is the visual
 * centre (nil = success, non-nil = danger — unmistakably different); three
 * regions (bank statement position · cash book · adjustment items). Finalise is
 * disabled until variance is nil and routes out-of-band (request verb). Statement
 * LINE import isn't in the primitive yet — the reconciled bank position + cash
 * book + adjustment items are rendered from what exists (render only what's real).
 */
import { Link, useParams } from "react-router";
import { useLedgerCurrentReconciliation, useRequestReconciliationFinalise } from "../../hooks/use-ledger-workbenches";
import { money } from "../../components/ledger/format";

const ADJ_LABEL: Record<string, string> = {
  outstanding_deposit: "Outstanding deposit", unpresented_instrument: "Unpresented instrument",
  credit_adjustment: "Credit adjustment", debit_adjustment: "Debit adjustment",
};

export function ReconciliationPage() {
  const { ledgerId } = useParams<{ ledgerId: string }>();
  const q = useLedgerCurrentReconciliation(ledgerId);
  const finalise = useRequestReconciliationFinalise();

  const back = <div className="row" style={{ marginBottom: 10 }}><Link to={`/app/trust/${ledgerId}`} className="muted tiny" style={{ textDecoration: "none" }}>← Cockpit</Link></div>;

  if (q.isLoading) return <div className="screen">{back}<div className="surface"><div className="surface__section"><div className="skeleton sk-line" style={{ width: "40%" }} /></div></div></div>;
  if (q.isError) return <div className="screen">{back}<div className="surface"><div className="surface__body"><div className="error-state"><svg className="mark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.4}><path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg><h3>Couldn’t load the reconciliation</h3><p>Statement position or cash-book postings didn’t load. Retry to continue.</p><button data-variant="primary" onClick={() => q.refetch()}>Retry</button></div></div></div></div>;

  const recon = q.data?.reconciliation ?? null;
  const items = q.data?.items ?? [];

  if (!recon) {
    return <div className="screen">{back}<div className="surface"><div className="surface__body"><div className="empty-state">
      <svg className="mark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.3}><path d="M12 3v12m0 0 4-4m-4 4-4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" /></svg>
      <h3>No reconciliation prepared</h3><p>A bookkeeper prepares a reconciliation (bank statement balance + outstanding items) over MCP. Once drafted, its variance and adjustments appear here.</p>
    </div></div></div></div>;
  }

  const nil = recon.variance_minor === 0;
  const isFinal = recon.status === "final";

  return (
    <div className="screen">{back}
      <div className="surface">
        <div className="surface__bar"><div className="surface__title"><strong>Bank reconciliation</strong><span className="sub">as at {recon.as_at_date} · {isFinal ? "final" : "draft"}</span></div><span className="spacer" /><Link to={`/app/print/${ledgerId}/reconciliation`} target="_blank"><button>Print preview</button></Link><span className={`pill ${isFinal ? "pill--approved" : "pill--draft"}`}><span className="dot" />{isFinal ? "reconciled" : "draft"}</span></div>

        {/* variance centre */}
        <div className="surface__section">
          <div className={`variance-tile ${nil ? "variance-tile--nil" : "variance-tile--open"}`}>
            {nil ? <svg className="icon" viewBox="0 0 24 24" style={{ width: 22, height: 22 }}><path d="M20 6 9 17l-5-5" /></svg> : null}
            <span className="v money">{money(recon.variance_minor)}</span>
            <span className="k">{nil ? "Variance nil" : "Variance"}</span>
            <span className="hint">{nil ? `Bank ${money(recon.bank_statement_balance_minor)} reconciles to the cash book after ${items.length} outstanding item${items.length === 1 ? "" : "s"}.` : `Cash book is ${money(Math.abs(recon.variance_minor))} ${recon.variance_minor > 0 ? "above" : "below"} the reconciled bank position. Match or adjust to reach nil.`}</span>
          </div>
        </div>

        {/* reconciliation statement */}
        <div className="surface__section">
          <dl className="particulars" style={{ gridTemplateColumns: "1fr max-content" }}>
            <dt>Bank statement balance</dt><dd className="money">{money(recon.bank_statement_balance_minor)}</dd>
            <dt>Cash book balance</dt><dd className="money">{money(recon.cashbook_balance_minor)}</dd>
            <dt style={{ fontWeight: 500, color: "var(--color-text-primary)" }}>Reconciled balance</dt><dd className="money" style={{ fontWeight: 500 }}>{money(recon.reconciled_balance_minor)}</dd>
          </dl>
        </div>

        {/* adjustments */}
        <div className="surface__section">
          <div className="section-head"><h3>Adjustments</h3><span className="count">{items.length}</span></div>
          {items.length === 0 ? <span className="muted tiny">No outstanding items.</span> : (
            <div className="recon-col"><div className="recon-col__body">
              {items.map((it) => (
                <div key={it.id} className="recon-line is-adj"><span className="desc">{ADJ_LABEL[it.kind] ?? it.kind} — {it.description}</span><span className="amt money">{money(it.amount_minor)}</span></div>
              ))}
            </div></div>
          )}
          {finalise.isError ? <div className="stale-banner" style={{ marginTop: 12 }}><svg className="icon" viewBox="0 0 24 24"><path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg><div>{(finalise.error as Error).message}</div></div> : null}
          {!isFinal ? (
            <div className="row" style={{ marginTop: 16 }}><span className="muted tiny">{nil ? "Finalising is out-of-band — a signatory authorises it from the approvals inbox." : "Finalise is disabled until variance is nil."}</span><span className="spacer" /><button data-variant="primary" disabled={!nil || finalise.isPending} onClick={() => finalise.mutate({ reconciliationId: recon.id })}>{finalise.isPending ? "Requesting…" : "Request authorisation to finalise"}</button></div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
